import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { Loader2 } from 'lucide-react';

/**
 * Client-side authentication guard component
 * Protects routes by checking for valid authentication
 * Redirects to login if user is not authenticated
 */
export function AuthGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [mounted, setMounted] = useState(false);

  // Set mounted to true on client after hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // If not loading and not authenticated, redirect to login
    if (mounted && !isLoading && !isAuthenticated) {
      console.log('🔒 AuthGuard: User not authenticated, redirecting to login');
      navigate('/auth/v2/login');
    }
  }, [mounted, isLoading, isAuthenticated, navigate]);

  // Show loading state during SSR and while checking authentication
  if (!mounted || isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render children if not authenticated
  // (will redirect in useEffect)
  if (!isAuthenticated) {
    return null;
  }

  // User is authenticated, render protected content
  console.log('✅ AuthGuard: User authenticated:', user?.email);
  return <>{children}</>;
}

